/**
 * @fileoverview Flow Editor Vuex actions for managing conversational flows.
 * @module store/modules/floweditor/actions
 */

import { ActionTree } from "vuex";
import { graph, rest } from "@/store/api";
import { RootState } from "@/store/types";

import { gql } from "@apollo/client/core";
import _ from "lodash";
import { FlowEditorState } from "./types";

const actions: ActionTree<FlowEditorState, RootState> = {
  /**
   * Fetches all flows, flow content, and flow triggers from backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<Object>} Resolved with flows data
   *
   * @example
   * const flows = await store.dispatch('FETCH_FLOWS');
   */
  FETCH_FLOWS: ({ state, commit }) => {
    commit("SET_FETCHING_FLOWS", true);
    return new Promise((resolve, reject) => {
      graph
        .query({
          query: gql`
            query {
              Bot {
                flows
                flowContent
                flowTrigger
              }
            }
          `,
          fetchPolicy: "network-only",
        })
        .then((payload) => {
          const data: any = _.cloneDeep(payload.data);
          commit("SET_FLOWS", data.Bot.flows);
          commit("SET_FLOW_CONTENT", data.Bot.flowContent);
          commit("SET_FLOW_TRIGGER", data.Bot.flowTrigger);
          resolve(data.Bot.flows);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          commit("SET_FETCHING_FLOWS", false);
        });
    });
  },
  /**
   * Fetches flow execution results with pagination and filtering.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} [payload.startDate] - Start date filter
   * @param {string} [payload.endDate] - End date filter
   * @param {number} [payload.limit] - Number of results to fetch
   * @param {number} [payload.offset] - Pagination offset
   * @param {string[]} [payload.flowIds] - Array of flow IDs to filter
   * @returns {Promise<Object>} Flow results data
   *
   * @example
   * const results = await store.dispatch('FETCH_ALL_FLOWS_RESULTS', {
   *   startDate: '2024-01-01',
   *   endDate: '2024-01-31',
   *   limit: 100,
   *   flowIds: ['flow1', 'flow2']
   * });
   */
  FETCH_ALL_FLOWS_RESULTS: ({ commit }, { startDate, endDate, limit, offset, flowIds }) => {
    return graph
      .query({
        query: gql`
          query (
            $startDate: String
            $endDate: String
            $flowIds: [String]
            $limit: Int
            $offset: Int
          ) {
            flowEditorAPI {
              getFlowResults(
                startDate: $startDate
                endDate: $endDate
                limit: $limit
                offset: $offset
                flowIds: $flowIds
              )
            }
          }
        `,
        variables: { startDate, endDate, limit, offset, flowIds },
        fetchPolicy: "network-only",
      })
      .then((response) => {
        // console.log("FETCH_ALL_FLOWS_RESULTS Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.getFlowResults");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Creates a new flow with specified mode.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} [payload.mode] - Flow mode (e.g., 'standard', 'advanced')
   * @returns {Promise<Object>} Created flow object
   *
   * @example
   * const newFlow = await store.dispatch('CREATE_FLOW', { mode: 'standard' });
   */
  CREATE_FLOW: ({ commit }, { mode }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($mode: String) {
            flowEditorAPI {
              createFlow(mode: $mode)
            }
          }
        `,
        variables: { mode },
      })
      .then((response) => {
        const data = _.get(response, "data.flowEditorAPI.createFlow");
        commit("ADD_FLOW", data);
        return data;
      });
  },
  /**
   * Saves a flow with its configuration.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.flowId - Flow ID to save
   * @param {Object} payload.flowObject - Flow configuration object
   * @param {boolean} [payload.publish] - Whether to publish the flow
   * @returns {Promise<Object>} Save response data
   *
   * @example
   * await store.dispatch('SAVE_FLOW', {
   *   flowId: 'flow123',
   *   flowObject: { name: 'My Flow', sections: [...] },
   *   publish: true
   * });
   */
  SAVE_FLOW: ({ commit }, { flowId, flowObject, publish }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($flowId: String!, $flowObject: JSON!, $publish: Boolean) {
            flowEditorAPI {
              saveFlow(flowId: $flowId, flowObject: $flowObject, publish: $publish)
            }
          }
        `,
        variables: { flowId, flowObject, publish },
      })
      .then((response) => {
        console.log("SAVE_FLOW Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.saveFlow");
        return data;
      });
  },
  /**
   * Saves multiple flows in batch.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {Object[]} payload.flows - Array of flow objects to save
   * @returns {Promise<Object>} Save response data
   *
   * @example
   * await store.dispatch('SAVE_FLOWS', {
   *   flows: [flow1, flow2, flow3]
   * });
   */
  SAVE_FLOWS: ({ state, commit }, { flows }) => {
    console.log("SAVE_FLOWS: ", flows);
    return graph
      .mutate({
        mutation: gql`
          mutation ($flows: [JSON]!) {
            flowEditorAPI {
              saveFlows(flows: $flows)
            }
          }
        `,
        variables: { flows },
      })
      .then((response) => {
        console.log("SAVE_FLOWS Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.saveFlows");
        return data;
      });
  },
  /**
   * Deletes a flow by ID.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.flowId - Flow ID to delete
   * @returns {Promise<Object>} Deletion response data
   *
   * @example
   * await store.dispatch('DELETE_FLOW', { flowId: 'flow123' });
   */
  DELETE_FLOW: ({ commit }, { flowId }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($flowId: String!) {
            flowEditorAPI {
              deleteFlow(flowId: $flowId)
            }
          }
        `,
        variables: { flowId },
      })
      .then((response) => {
        console.log("DELETE_FLOW Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.deleteFlow");
        commit("DELETE_FLOW", flowId);
        return data;
      });
  },

  /**
   * Deletes a suggested answer from a question.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.suggestedAnswerId - Suggested answer ID to delete
   * @returns {Promise<Object>} Deletion response or error
   *
   * @example
   * await store.dispatch('DELETE_SUGGESTED_ANSWER', { suggestedAnswerId: 'answer123' });
   */
  DELETE_SUGGESTED_ANSWER: ({ commit }, { suggestedAnswerId }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($suggestedAnswerId: String!) {
            flowEditorAPI {
              deleteSuggestedAnswer(suggestedAnswerId: $suggestedAnswerId)
            }
          }
        `,
        variables: { suggestedAnswerId },
      })
      .then((response) => {
        console.log("DELETE_FLOW Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.deleteSuggestedAnswer");
        return data;
      })
      .catch((err) => {
        return err;
      });
  },
  /**
   * Creates a new section in a flow.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {Object} payload.flow - Flow object to add section to
   * @returns {Promise<Object>} Created section data
   *
   * @example
   * const section = await store.dispatch('CREATE_NEW_SECTION', {
   *   flow: { flowId: 'flow123', name: 'My Flow' }
   * });
   */
  CREATE_NEW_SECTION: ({ commit }, { flow }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($flow: JSON!) {
            flowEditorAPI {
              createStandardFlowSection(flow: $flow)
            }
          }
        `,
        variables: { flow },
      })
      .then((response) => {
        console.log("CREATE_NEW_SECTION Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.createStandardFlowSection");
        // commit("ADD_FLOW_SECTION", {
        //   flow,
        //   section: data,
        // })
        return data;
      });
  },
  /**
   * Creates a new question in a flow section.
   *
   * @param {Object} store - Vuex store
   * @param {Object} payload - Action payload
   * @param {Object} payload.section - Section object to add question to
   * @param {string} payload.newQuestionType - Type of question to create
   * @returns {Promise<Object>} Created question data
   *
   * @example
   * const question = await store.dispatch('CREATE_NEW_QUESTION', {
   *   section: { sectionId: 'section123' },
   *   newQuestionType: 'text'
   * });
   */
  CREATE_NEW_QUESTION: (store, { section, newQuestionType }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($section: JSON!, $newQuestionType: String!) {
            flowEditorAPI {
              createStandardFlowQuestion(section: $section, newQuestionType: $newQuestionType)
            }
          }
        `,
        variables: { section, newQuestionType },
      })
      .then((response) => {
        console.log("CREATE_NEW_QUESTION Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.createStandardFlowQuestion");
        return data;
      });
  },
  /**
   * Creates a new suggested answer for a question.
   *
   * @param {Object} store - Vuex store
   * @param {Object} payload - Action payload
   * @param {string} payload.questionId - Question ID to add answer to
   * @param {string} payload.answerType - Type of suggested answer
   * @param {Object} payload.question - Question object
   * @returns {Promise<Object>} Created suggested answer data
   *
   * @example
   * const answer = await store.dispatch('CREATE_NEW_SUGGESTED_ANSWER', {
   *   questionId: 'q123',
   *   answerType: 'quick_reply',
   *   question: { ... }
   * });
   */
  CREATE_NEW_SUGGESTED_ANSWER: (store, { questionId, answerType, question }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($questionId: String!, $answerType: String!, $question: JSON!) {
            flowEditorAPI {
              createStandardFlowSuggestedAnswer(
                questionId: $questionId
                answerType: $answerType
                question: $question
              )
            }
          }
        `,
        variables: { questionId, answerType, question },
      })
      .then((response) => {
        console.log("CREATE_NEW_SUGGESTED_ANSWER Response: ", response);
        const data = _.get(response, "data.flowEditorAPI.createStandardFlowSuggestedAnswer");
        return data;
      });
  },
  /**
   * Previews a flow execution on specified channel.
   *
   * @param {Object} store - Vuex store
   * @param {Object} payload - Action payload
   * @param {string} payload.sectionId - Section ID to preview
   * @param {string} payload.sourceType - Source channel type ('twilio', 'webchat', etc.)
   * @param {string} payload.senderId - Sender ID for preview
   * @param {string} payload.recipientId - Recipient ID for preview
   * @returns {Promise<Object>} Preview response data
   *
   * @example
   * await store.dispatch('PREVIEW_FLOW', {
   *   sectionId: 'section123',
   *   sourceType: 'webchat',
   *   senderId: 'user123',
   *   recipientId: 'bot456'
   * });
   */
  PREVIEW_FLOW: (store, { sectionId, sourceType, senderId, recipientId }) => {
    // workflow_trigger_twilio_preview;
    // workflow_trigger_webchat_preview;
    // workflow_trigger_telegram_preview;
    // workflow_trigger_facebook_preview;
    if (sourceType === "twilio") {
      return rest("post", "workflow_trigger_sms_preview", {
        sectionId: sectionId,
        name: "joshua",
        phoneNumber: "+123456789",
        triggerTime: 1546409017,
      });
    } else if (sourceType === "webchat") {
      return rest("post", "workflow_trigger_webchat_preview", {
        sectionId: sectionId,
        sender: {
          id: senderId,
        },
        recipient: {
          id: recipientId,
        },
        triggerTime: 1546409017,
      });
    }
  },
  /**
   * Deletes a question from a flow section.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.flowId - Flow ID
   * @param {string} payload.sectionId - Section ID
   * @param {string} payload.questionId - Question ID to delete
   * @returns {number} Index of deleted question
   *
   * @example
   * const index = store.dispatch('DELETE_FLOW_QUESTION', {
   *   flowId: 'flow123',
   *   sectionId: 'section456',
   *   questionId: 'q789'
   * });
   */
  DELETE_FLOW_QUESTION: ({ state, commit }, { flowId, sectionId, questionId }) => {
    const _flows = _.cloneDeep(state.flows);
    const _sectionIndex = _flows[flowId].sections.findIndex(
      (section) => section.sectionId === sectionId
    );
    if (_sectionIndex >= 0) {
      const _questionIndex = _flows[flowId].sections[_sectionIndex].questions.findIndex(
        (question) => question.questionId === questionId
      );
      _flows[flowId].sections[_sectionIndex].questions.splice(_questionIndex, 1);
      commit("SET_FLOWS", _flows);
      return _questionIndex;
    }
  },
  /**
   * Previews a reply in flow conversation.
   *
   * @param {Object} store - Vuex store
   * @param {Object} payload - Action payload
   * @param {string} [payload.message] - Message text to preview
   * @param {string} payload.sourceType - Source channel type
   * @param {Object} [payload.postback] - Postback data for button clicks
   * @param {string} payload.senderId - Sender ID
   * @param {string} payload.recipientId - Recipient ID
   * @returns {Promise<Object>} Preview response data
   *
   * @example
   * await store.dispatch('PREVIEW_REPLY', {
   *   message: 'Hello',
   *   sourceType: 'webchat',
   *   senderId: 'user123',
   *   recipientId: 'bot456'
   * });
   */
  PREVIEW_REPLY: (store, { message, sourceType, postback, senderId, recipientId }) => {
    if (sourceType === "twilio") {
      return rest("post", "receive_sms", {
        message: message,
        phoneNumber: "+123456789",
        preview: true,
      });
    } else if (sourceType === "webchat") {
      if (message) {
        // TODO: @REMY, make sure senderId = user of the dashboard
        // recipientId = keyreply
        return rest("post", "webchat", {
          message: {
            text: message,
            isPreview: true,
          },
          sender: {
            id: senderId,
          },
          recipient: {
            id: recipientId,
          },
        });
      } else {
        return rest("post", "webchat", {
          postback: {
            ...postback,
            isPreview: true,
          },
          sender: {
            id: senderId,
          },
          recipient: {
            id: recipientId,
          },
        });
      }
    }
  },
  /**
   * Gets WhatsApp flow preview URL.
   *
   * @param {Object} store - Vuex store
   * @param {Object} payload - Action payload
   * @param {Object} payload.data - Flow data for preview
   * @returns {Promise<Object>} Preview URL response
   *
   * @example
   * const url = await store.dispatch('GET_FLOW_PREVIEW_URL', {
   *   data: { flowId: 'flow123' }
   * });
   */
  GET_FLOW_PREVIEW_URL: (store, payload) => {
    return rest("post", "whatsapp_get_flow_json", payload.data)
  },
};
export default actions;
